import { axiosInstance } from "../store";
import IUser from "../dtos/IUser";
import IBranch from "../dtos/IBranch";
import { handlStatusCodeMessage } from "../helpers";
import { toast } from "react-toastify";
import IOrderAddress from "../dtos/IOrderAddress";
import IAddress from "../dtos/IAddress";
import { logout } from "../store/oidc";

export function validateUser(user: any): Promise<{
  userValidated: boolean;
  service_account_id: number | null;
  my_branches: IBranch[] | null;
  user_id: number | null;
  storefronts: any;
}> {
  // console.log('Validating user with API:', user);
  
  // Check if we have the required info to validate the user
  if (!user || !user.idTokenParsed || !user.idTokenParsed.email) {
    console.error('Missing required user information for validation', user);
    return Promise.resolve({
      userValidated: false,
      service_account_id: null,
      my_branches: [],
      user_permissions: [],
      user_id: null,
      storefronts: [],
      error: 'Missing required user information for validation'
    });
  }
  
  // Add auth token to headers if available
  let headers = {};
  if (user.token) {
    headers = {
      Authorization: `Bearer ${user.token}`
    };
  }

  return axiosInstance
    .get(
      `${process.env.REACT_APP_API_URL}account/auth/sign-in?email=${user.idTokenParsed.email}&subject_id=${user.subject}&component_id=${process.env.REACT_APP_COMPONENT_ID}`,
      { headers }
    )
    .then((res) => {
      // console.log('User validation API response:', res);
      if (res.status == 200) {
        return {
          userValidated: true,
          service_account_id: res.data.user_info.service_account_id,
          my_branches: res.data.user_info.branches,
          user_permissions: res.data.user_permissions,
          user_id: res.data.user_info.user_id,
          user_profile: res.data.user_profile,
          storefronts: res.data.storefronts,
          user_email: res.data.user_info.email,
          user_first_name: res.data.user_info.first_name,
          user_last_name: res.data.user_info.last_name,
          user_groups: res.data.user_info.user_groups,
        };
      } else {
        console.warn('User validation API returned non-200 status:', res.status);
        return {
          userValidated: false,
          service_account_id: null,
          my_branches: [],
          user_permissions: [],
          user_id: null,
          storefronts: [],
        };
      }
    })
    .catch((err) => {
      console.error('User validation API error:', err);
      let errorMessage = 'Unknown error';
      let isWrongPortalError = false;
      logout();
      if (err.response) {
        errorMessage = err.response.data?.message || `Error ${err.response.status}`;
        
        // Check for the specific "No profile found" error with UM-025 code
        if (err.response.data?.errorCode === 'UM-025' || 
            (err.response.data?.detail === 'No profile found' && err.response.status === 404)) {
          isWrongPortalError = true;
          // console.log('Detected wrong portal error - UM-025');
        } else {
          handlStatusCodeMessage({
            code: err.response.status,
            entity: "user",
            text: err.response.data.message,
          });
        }
      } else if (err.message) {
        errorMessage = err.message;
      }
      
      return {
        userValidated: false,
        service_account_id: null,
        my_branches: [],
        user_permissions: [],
        user_id: null,
        storefronts: [],
        error: errorMessage,
        isWrongPortalError
      };
    });
}

export function sendInvitation(invitation: any): Promise<boolean> {
  return axiosInstance
    .post(`${process.env.REACT_APP_API_URL}account/invitation/send?component_id=2`, invitation)
    .then((res) => {
      if (res.status == 200) {
        handlStatusCodeMessage({ code: res.status, entity: "user" });
        return true;
      } else {
        handlStatusCodeMessage({ code: res.status, entity: "user" });
        return false;
      }
    })
    .catch((err) => {
      handlStatusCodeMessage({ code: err.response.status, entity: "user" });
      return false;
    });
}

export function validateInvitation(token: string): Promise<boolean> {
  return axiosInstance
    .get(`${process.env.REACT_APP_API_URL}account/invitation/validate/${token}?component_id=2`)
    .then((res) => (res.status == 200 ? true : false))
    .catch((err) => {
      handlStatusCodeMessage({ code: err.response.status, entity: "user" });
      return false;
    });
}

export function fetchUser(subject_id: IUser["user_id"], component_id = 2): Promise<any> {
  return axiosInstance
    .get(`${process.env.REACT_APP_API_URL}account/user/${subject_id}?component_id=${component_id}&storefront_id=1`)
    .then((res) => (res.status == 200 ? res.data : false))
    .catch((err) => {
      handlStatusCodeMessage({ code: err.response.status, entity: "user" });
      return false;
    });
}

export function updateUserNotes(subject_id: IUser["user_id"], notes: string): Promise<any> {
  return axiosInstance
    .put(`${process.env.REACT_APP_API_URL}account/user/${subject_id}/notes`, {
      notes: notes
    })
    .then((res) => (res.status == 200 ? res.data : false))
    .catch((err) => {
      handlStatusCodeMessage({ code: err.response.status, entity: "user notes" });
      return false;
    });
}

export function uploadUserDocuments(subject_id: IUser["user_id"], documents: File[]): Promise<any> {
  const formData = new FormData();
  documents.forEach((file, index) => {
    formData.append('documents', file);
  });

  return axiosInstance
    .put(`${process.env.REACT_APP_API_URL}account/user/${subject_id}/documents`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    })
    .then((res) => (res.status == 200 ? res.data : false))
    .catch((err) => {
      handlStatusCodeMessage({ code: err.response.status, entity: "user documents" });
      return false;
    });
}

export function deleteUser(subject_id: IUser["user_id"]): Promise<any> {
  return axiosInstance
    .delete(`${process.env.REACT_APP_API_URL}account/user/${subject_id}?component_id=2`)
    .then((res) => (res.status == 200 ? true : false))
    .catch((err) => {
      handlStatusCodeMessage({ code: err.response.status, entity: "user" });
      return false;
    });
}

export function deactivateUserCard(id: IUser["user_id"], cardId: number): Promise<any> {
  return axiosInstance
    .post(
      `${process.env.REACT_APP_API_URL}account/access_card/deactivate_card/?user_id=${id}&access_card_id=${cardId}`
    )
    .then((res) => (res.status == 200 ? true : false))
    .catch((err) => {
      handlStatusCodeMessage({ code: err.response.status, entity: "user" });
      return false;
    });
}

export function deactivateAuthUserCard(id: IUser["user_id"], cardId: number): Promise<any> {
  return axiosInstance
    .post(
      `${process.env.REACT_APP_API_URL}account/access_card/deactivate_my_card/?user_id=${id}&access_card_id=${cardId}`
    )
    .then((res) => (res.status == 200 ? true : false))
    .catch((err) => {
      handlStatusCodeMessage({ code: err.response.status, entity: "user" });
      return false;
    });
}

export function requestUserCard(id: IUser["user_id"]): Promise<any> {
  return axiosInstance
    .post(`${process.env.REACT_APP_API_URL}account/access_card/user?user_id=${id}`)
    .then((res) => (res.status == 200 ? true : false))
    .catch((err) => {
      handlStatusCodeMessage({ code: err.response.status, entity: "user" });
      return false;
    });
}
export function resendInvitation(id: IUser["user_id"]): Promise<any> {
  return axiosInstance
    .post(`${process.env.REACT_APP_API_URL}account/invitation/resend?component_id=2&user_id=${id}`)
    .then((res) => (res.status == 200 ? true : false))
    .catch((err) => {
      handlStatusCodeMessage({ code: err.response.status, entity: "user" });
      return false;
    });
}

export function updateUser(id: IUser["user_id"], userGroupIds: number[]): Promise<any> {
  return axiosInstance
    .put(`${process.env.REACT_APP_API_URL}account/user/${id}?component_id=2`, {
      user_group_ids_list: userGroupIds,
    })
    .then((res) => (res.status == 200 ? toast.success("changes saved successfully.") : false))
    .catch((err) => {
      handlStatusCodeMessage({ code: err.response.status, entity: "user" });
      return false;
    });
}
export function fetchUsers(
  componentId: any = process.env.REACT_APP_ASSIGNED_SERVICE_COMPONENT_ID,
  page = 0,
  size = 10,
  sort = "",
  name = "",
  email = "",
  firstName = "",
  lastName = "",
  searchTerm = "",
  userId?: number | string,
  membershipLevel = "",
  case_manager_id?: number,
): Promise<{ users: IUser[]; totalUsersCount: number }> {

  const params = new URLSearchParams();
  params.append('component_id', componentId?.toString() || '');
  params.append('page', page.toString());
  params.append('size', size.toString());
  params.append('email', email);
  params.append('first_name', firstName);
  params.append('last_name', lastName);
  params.append('sort', sort);
  
  if (name) params.append('name', name);
  if (case_manager_id) params.append('case_manager_id', case_manager_id.toString());
  if (searchTerm) params.append('full_name', searchTerm);
  if (userId) params.append('user_id', userId.toString());
  if (membershipLevel) params.append('membership_level', membershipLevel);
  
  return axiosInstance
    .get(
      `${process.env.REACT_APP_API_URL}account/user?${params.toString()}`
    )
    .then((res) =>
      res.status == 200
        ? {
            users: res.data,
            totalUsersCount: parseInt(res.headers["x-total-count"]),
          }
        : {
            users: [],
            totalUsersCount: 0,
          }
    )
    .catch((err) => {
      handlStatusCodeMessage({ code: err.response.status, entity: "user" });
      return {
        users: [],
        totalUsersCount: 0,
      };
    });
}
export function fetchSales(): Promise<any> {
  return axiosInstance
    .get(`${process.env.REACT_APP_API_URL}account/sales-staff?component_id=4`)
    .then((res) => {
      return res.data;
    })
    .catch((err) => {
      handlStatusCodeMessage({ code: err.response.status, entity: "user" });
      return [];
    });
}

export function fetchCaseManagers(): Promise<any> {
  const url = `${process.env.REACT_APP_API_URL}account/user?component_id=2&group_id=40`;
  return axiosInstance
    .get(url)
    .then((res) => {
      // Handle 204 No Content or empty responses
      if (res.status === 204 || !res.data) {
        return [];
      }
      return res.data;
    })
    .catch((err) => {
      handlStatusCodeMessage({ code: err.response.status, entity: "user" });
      return [];
    });
}

export function assignSales(userId: number, salesId: number) {
  return axiosInstance
    .post(`${process.env.REACT_APP_API_URL}account/user/assign/sales`, {
      user_id: userId,
      sales_id: salesId,
    })
    .then((res) => res.data)
    .catch((err) => {
      handlStatusCodeMessage({ code: err.response.status, entity: "user" });
      return undefined;
    });
}

export function assignCaseManager(userId: number, caseManagerId: number) {
  return axiosInstance
    .post(`${process.env.REACT_APP_API_URL}account/user/assign/case-manager`, {
      user_id: userId,
      case_manager_id: caseManagerId,
    })
    .then((res) => res.data)
    .catch((err) => {
      handlStatusCodeMessage({ code: err.response.status, entity: "user" });
      return undefined;
    });
}

export function fetchDoctors(page: number, user_id: string, searchTerm: string): Promise<any> {
  const url = `${process.env.REACT_APP_API_URL}account/user/doctors?page=${page}&size=30&full_name=${searchTerm}&user_id=${user_id}`;
  
  return axiosInstance
    .get(url)
    .then((res) => {
      return res.data;
    })
    .catch((err) => {
      handlStatusCodeMessage({ code: err.response.status, entity: "user" });
      return [];
    });
}

export function assignDoctor(userId: number, doctorId: number) {
  return axiosInstance
    .post(`${process.env.REACT_APP_API_URL}account/user/assign/doctor`, {
      user_id: userId,
      doctor_id: doctorId,
    })
    .then((res) => res.data)
    .catch((err) => {
      handlStatusCodeMessage({ code: err.response.status, entity: "user" });
      return undefined;
    });
}

export function searchUser(
  firstName = "",
  lastName = "",
  mobileNumber = "",
  email = ""
): Promise<IUser[]> {
  let containsSpace = /\s/g.test(firstName);
  if (containsSpace) {
    let splitted = firstName.split(" ");
    firstName = splitted[0];
    lastName = splitted[1];
  }
  return axiosInstance
    .get(
      `${process.env.REACT_APP_API_URL}account/user?component_id=${process.env.REACT_APP_COMPONENT_ID}&page=0&size=1000&first_name=${firstName}&last_name=${lastName}&mobile=${mobileNumber}&email=${email}`
    )
    .then((res) => {
      if (res.status == 200) {
        return res.data;
      } else {
        return [];
      }
    })
    .catch((err) => {
      handlStatusCodeMessage({ code: err.response.status, entity: "user" });
      return [];
    });
}
export function fetchUserAddress(subjectId = ""): Promise<IOrderAddress | null> {
  return axiosInstance
    .get(
      `${process.env.REACT_APP_API_URL}account/ecommerce-profile/${subjectId}/addresses?component_id=4`
    )
    .then((res) => {
      if (res.status == 200) {
        let defaultAddress = res.data.find((address: IOrderAddress) => address.is_default);
        if (defaultAddress) {
          return defaultAddress;
        } else {
          return res.data[0];
        }
      } else {
        return null;
      }
    })
    .catch((err) => {
      handlStatusCodeMessage({ code: err.response.status, entity: "user" });
      return null;
    });
}

export function createUserAddress(subject_id: string, address: IAddress): Promise<IAddress | null> {
  return axiosInstance
    .post(
      `${process.env.REACT_APP_API_URL}account/ecommerce-profile/${subject_id}/add-address?component_id=4`,
      address
    )
    .then((res) => {
      if (res.status == 200) {
        handlStatusCodeMessage({ code: res.status, entity: "user" });
        return res.data;
      } else {
        handlStatusCodeMessage({ code: res.status, entity: "user" });
        return null;
      }
    })
    .catch((err) => {
      handlStatusCodeMessage({ code: err.response.status, entity: "user" });
      return null;
    });
}
export function updateUserAddress(
  subject_id: string,
  address: IOrderAddress
): Promise<IAddress | null> {
  return axiosInstance
    .put(
      `${process.env.REACT_APP_API_URL}account/ecommerce-profile/address/update/${address.id}/user/${subject_id}?component_id=4`,
      address
    )
    .then((res) => {
      if (res.status == 200) {
        handlStatusCodeMessage({ code: res.status, entity: "user" });
        return res.data;
      } else {
        handlStatusCodeMessage({ code: res.status, entity: "user" });
        return null;
      }
    })
    .catch((err) => {
      handlStatusCodeMessage({ code: err.response.status, entity: "user" });
      return null;
    });
}

export function deleteUserAddress(
  subject_id: string,
  address: IOrderAddress
): Promise<IAddress | null> {
  return axiosInstance
    .delete(
      `${process.env.REACT_APP_API_URL}account/ecommerce-profile/address/delete/${address.id}/user/${subject_id}?component_id=4`
    )
    .then((res) => {
      if (res.status == 200) {
        handlStatusCodeMessage({ code: res.status, entity: "user" });
        return res.data;
      } else {
        handlStatusCodeMessage({ code: res.status, entity: "user" });
        return null;
      }
    })
    .catch((err) => {
      handlStatusCodeMessage({ code: err.response.status, entity: "user" });
      return null;
    });
}

export function fetchUserAddresses(subject_id = ""): Promise<IOrderAddress> {
  return axiosInstance
    .get(
      `${process.env.REACT_APP_API_URL}account/ecommerce-profile/${subject_id}/addresses?component_id=4`
    )
    .then((res) => (res.status == 200 ? res.data : []))
    .catch((err) => {
      handlStatusCodeMessage({ code: err.response.status, entity: "user" });
      return [];
    });
}

export function fetchQuickProfile(user_id: string): Promise<any> {
  return new Promise((resolve, reject) => {
    axiosInstance
      .get(`${process.env.REACT_APP_API_URL}account/ecommerce-profile/${user_id}/quick-profile`)
      .then((res) => {
        resolve(res.data);
      })
      .catch((err) => {
        reject([]);
      });
  });
}

export function getUser(subject_id: "") {
  return axiosInstance
    .get(`${process.env.REACT_APP_API_URL}account/v2/user/id/${subject_id}`)
    .then((res) => {
      return res.data;
    })
    .catch((err) => {
      return { data: err.response.data, code: err.response.code };
    });
}

export function getSubjectIdByUserId(userId: string | number): Promise<any> {
  return axiosInstance
    .get(`${process.env.REACT_APP_API_URL}account/user/${userId}/basic`)
    .then((res) => {
      return res.data;
    })
    .catch((err) => {
      handlStatusCodeMessage({ code: err.response.status, entity: "user" });
      return null;
    });
}

export function fetchMembershipTypes(): Promise<any[]> {
  return axiosInstance
    .get(`${process.env.REACT_APP_API_URL}account/lookup/membership-level`)
    .then((res) => {
      if (res.status === 200) {
        return res.data;
      } else {
        return [];
      }
    })
    .catch((err) => {
      handlStatusCodeMessage({ code: err.response.status, entity: "membership" });
      return [];
    });
}

export function updateUserMembershipLevel(
  userId: number,
  subjectId: string,
  membershipLevelId: number
): Promise<any> {
  return axiosInstance
    .put(
      `${process.env.REACT_APP_API_URL}account/user/${userId}/membership-level?subject_id=${subjectId}`,
      {
        membershipLevelId: membershipLevelId,
      }
    )
    .then((res) => {
      if (res.status === 200) {
        handlStatusCodeMessage({ code: res.status, entity: "membership" });
        return res.data;
      } else {
        handlStatusCodeMessage({ code: res.status, entity: "membership" });
        return null;
      }
    })
    .catch((err) => {
      handlStatusCodeMessage({ code: err.response.status, entity: "membership" });
      return null;
    });
}

export async function getUserBasicActiveSubscription(userId: number) {
  return axiosInstance
    .get(`${process.env.REACT_APP_SUBSCRIPTION_API_BASE_URL}/subscriptions/active/${userId}/basic`)
    .then((res) => {
      if (res.status === 200) {
        return res.data;
      } else {
        return null;
      }
    })
    .catch((err) => {
      // handlStatusCodeMessage({ code: err.response?.status, entity: "subscription" });
      return null;
    });
}
